import React, { forwardRef, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import { DrugSearchResult } from "@/lib/api";
import { useDrugSearch } from "@/hooks/useDrugSearch";
import { UI_CONSTANTS } from "@/lib/constants";

interface DrugSearchInputProps {
    value: string;
    onChange: (drug: DrugSearchResult) => void;
    onCustomInput?: (value: string) => void;
    placeholder?: string;
    className?: string;
    onEnterNext?: () => void;
    subtitle?: string;
}

const DrugSearchInput = forwardRef<HTMLInputElement, DrugSearchInputProps>(
    ({ value, onChange, onCustomInput, placeholder = "Search medicine...", className, onEnterNext, subtitle }, ref) => {
        const [inputValue, setInputValue]       = useState(value ?? "");
        // searchQuery is separate from inputValue: it only updates when the user
        // types, not when a drug is selected. This prevents an extra search API
        // call firing for the brand name after selection.
        const [searchQuery, setSearchQuery]     = useState("");
        const [isOpen, setIsOpen]               = useState(false);
        const [isFocused, setIsFocused]         = useState(false);
        const [highlightedIndex, setHighlightedIndex] = useState(0);

        const containerRef = useRef<HTMLDivElement>(null);
        const dropdownRef  = useRef<HTMLDivElement>(null);
        const optionRefs   = useRef<(HTMLDivElement | null)[]>([]);

        const { drugs, isLoading } = useDrugSearch(searchQuery);

        useEffect(() => { setInputValue(value ?? ""); }, [value]);
        useEffect(() => { setHighlightedIndex(0); }, [drugs]);

        // Scroll highlighted item into view
        useEffect(() => {
            if (isOpen && optionRefs.current[highlightedIndex] && dropdownRef.current) {
                const el       = optionRefs.current[highlightedIndex];
                const dropdown = dropdownRef.current;
                if (el) {
                    if (el.offsetTop < dropdown.scrollTop)
                        dropdown.scrollTop = el.offsetTop;
                    else if (el.offsetTop + el.offsetHeight > dropdown.scrollTop + dropdown.clientHeight)
                        dropdown.scrollTop = el.offsetTop + el.offsetHeight - dropdown.clientHeight;
                }
            }
        }, [highlightedIndex, isOpen]);

        useEffect(() => {
            optionRefs.current = optionRefs.current.slice(0, drugs.length);
            if (highlightedIndex >= drugs.length && drugs.length > 0)
                setHighlightedIndex(drugs.length - 1);
        }, [drugs.length, highlightedIndex]);

        const handleSelect = (drug: DrugSearchResult, e: React.MouseEvent) => {
            e.preventDefault();
            setInputValue(drug.brand_name);
            setSearchQuery("");   // stop searching — don't fire a search for the brand name
            onChange(drug);
            setIsOpen(false);
            if (onEnterNext) setTimeout(onEnterNext, 100);
        };

        const showDropdown =
            isOpen && inputValue.trim().length >= UI_CONSTANTS.MIN_DRUG_SEARCH_LENGTH;

        const getDropdownStyle = (): React.CSSProperties => {
            if (!containerRef.current) return {};
            const rect = containerRef.current.getBoundingClientRect();
            const minWidth = Math.max(rect.width, 280);
            const dropdownHeight = Math.min(drugs.length * 56 + 16, 256);
            const wouldOverflowBottom = rect.bottom + dropdownHeight > window.innerHeight - 20;
            return {
                position: "fixed",
                minWidth: `${minWidth}px`,
                width: `${rect.width}px`,
                ...(wouldOverflowBottom
                    ? { bottom: window.innerHeight - rect.top + 4, top: "auto" }
                    : { top: rect.bottom + 4 }),
                left: rect.left,
            };
        };

        return (
            <div ref={containerRef} className="relative">
                {/* Borderless input — border only on focus */}
                <input
                    ref={ref}
                    type="text"
                    value={inputValue}
                    autoComplete="on"
                    placeholder={ placeholder}
                    className={cn(
                        "w-full text-xs font-semibold text-foreground transition-all duration-150",
                        "outline-none px-1 py-0.5",
                        "outline-none placeholder:text-muted-foreground/40 placeholder:font-normal",
                        className
                    )}
                    onChange={(e) => {
                        const val = e.target.value;
                        setInputValue(val);
                        setSearchQuery(val);  // only user typing drives the search
                        onCustomInput?.(val);
                        setIsOpen(val.trim().length >= UI_CONSTANTS.MIN_DRUG_SEARCH_LENGTH);
                    }}
                    onFocus={() => {
                        setIsFocused(true);
                        if (inputValue.trim().length >= UI_CONSTANTS.MIN_DRUG_SEARCH_LENGTH)
                            setIsOpen(true);
                    }}
                    onClick={() => {
                        if (inputValue.trim().length >= UI_CONSTANTS.MIN_DRUG_SEARCH_LENGTH)
                            setIsOpen(true);
                    }}
                    onBlur={() => {
                        setIsFocused(false);
                        setTimeout(() => setIsOpen(false), 200);
                    }}
                    onKeyDown={(e) => {
                        if (!isOpen && drugs.length > 0 &&
                            inputValue.trim().length >= UI_CONSTANTS.MIN_DRUG_SEARCH_LENGTH) {
                            setIsOpen(true);
                            setHighlightedIndex(0);
                            return;
                        }
                        if (!isOpen) {
                            if (e.key === "Enter" && onEnterNext) { e.preventDefault(); onEnterNext(); }
                            return;
                        }
                        if (e.key === "ArrowDown") {
                            e.preventDefault();
                            setHighlightedIndex((p) => Math.min(p + 1, drugs.length - 1));
                        } else if (e.key === "ArrowUp") {
                            e.preventDefault();
                            setHighlightedIndex((p) => Math.max(p - 1, 0));
                        } else if (e.key === "Enter") {
                            e.preventDefault();
                            if (drugs.length > 0 && highlightedIndex >= 0 && highlightedIndex < drugs.length) {
                                const drug = drugs[highlightedIndex];
                                setInputValue(drug.brand_name);
                                setSearchQuery("");   // stop searching after keyboard selection too
                                onChange(drug);
                                setIsOpen(false);
                                if (onEnterNext) setTimeout(onEnterNext, 100);
                            } else if (onEnterNext) {
                                onEnterNext();
                            }
                        } else if (e.key === "Escape") {
                            setIsOpen(false);
                        } else if (e.key === "Tab") {
                            setIsOpen(false);
                        }
                    }}
                />

                { !showDropdown && subtitle && (
                    <span
                        className="absolute left-1 right-1 text-[10px] text-muted-foreground/70 italic leading-none truncate pointer-events-none z-10"
                        style={{ top: "100%", paddingTop: 2 }}
                        title={subtitle}
                    >
                        {subtitle}
                    </span>
                )}

                {showDropdown && createPortal(
                    <div
                        ref={dropdownRef}
                        className="z-[9999] bg-popover shadow-lg border border-border rounded-md max-h-64 overflow-auto text-sm ring-1 ring-border"
                        style={getDropdownStyle()}
                    >
                        {isLoading && (
                            <div className="px-3 py-4 flex items-center justify-center">
                                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                            </div>
                        )}

                        {!isLoading && drugs.length > 0 && drugs.map((drug, i) => (
                            <div
                                key={drug.id}
                                ref={(el) => { optionRefs.current[i] = el; }}
                                className={cn(
                                    "px-3 py-2 cursor-pointer transition-colors border-b border-border/40 last:border-0",
                                    i === highlightedIndex
                                        ? "bg-primary text-primary-foreground"
                                        : "hover:bg-accent text-foreground"
                                )}
                                onMouseEnter={() => setHighlightedIndex(i)}
                                onMouseDown={(e) => handleSelect(drug, e)}
                            >
                                <div className="flex items-center gap-1.5 justify-between">
                                    <div className="flex items-center gap-1.5 min-w-0">
                                        {!!drug.is_favourite && <span className="text-yellow-500 text-xs shrink-0">★</span>}
                                        <span className="font-semibold text-sm truncate">{drug.brand_name}</span>
                                    </div>
                                    {drug.form && (
                                        <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-current/10 capitalize shrink-0">
                                            {drug.form}
                                        </span>
                                    )}
                                </div>
                                {drug.composition_raw && (
                                    <div className={cn(
                                        "text-xs mt-0.5",
                                        i === highlightedIndex ? "text-primary-foreground/75" : "text-muted-foreground"
                                    )}>
                                        {drug.composition_raw}
                                    </div>
                                )}
                            </div>
                        ))}

                        {!isLoading && drugs.length === 0 && (
                            <div className="px-3 py-3 text-muted-foreground text-center text-xs italic">
                                No results found
                            </div>
                        )}
                    </div>,
                    document.body
                )}
            </div>
        );
    }
);

DrugSearchInput.displayName = "DrugSearchInput";
export default DrugSearchInput;